import { SizeGuideButton } from '@app/components/SizeGuide';
import type {
  Gender,
  MeasurementUnit,
  SizeGuideData,
} from '@app/components/SizeGuide/types';
import type { InventoryItem } from '@app/types/shop-bff/types';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { ColorFieldConfigurator } from './ColorFieldConfigurator';
import { SimpleFieldConfigurator } from './FieldConfigurators/SimpleFieldConfigurator';
import type { FieldType } from './FieldConfigurators/types';
import { SizeFieldConfigurator } from './SizeFieldConfigurator';
import {
  ConfiguratorOption,
  getGenericOptionConfig,
  getTranslatedString,
} from './utils';

export interface OptionFieldDelegatorProps {
  option: ConfiguratorOption;
  filterValues: Array<{
    value: ConfiguratorOption['values'][number];
    inventoryItem: InventoryItem;
  }>;
  currentValue: string;
  locale: string;
  errors: Record<string, string>;
  currentProductTypeId?: string;
  workId?: number;
  sizeGuideData?: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
}

export const OptionFieldDelegator: React.FC<OptionFieldDelegatorProps> = ({
  option,
  filterValues,
  currentValue,
  locale,
  errors,
  currentProductTypeId,
  workId,
  sizeGuideData,
  initialGender,
  initialMeasurementUnit,
}) => {
  const label = getTranslatedString(option.publicName, option.key, locale);

  switch (option.frontendControl) {
    case 'color': {
      return (
        <ColorFieldConfigurator
          locale={locale}
          option={option}
          currentValue={currentValue}
          filterValues={filterValues}
          errors={errors}
        />
      );
    }
    case 'size': {
      return (
        <SizeFieldConfigurator
          locale={locale}
          option={option}
          currentValue={currentValue}
          filterValues={filterValues}
          errors={errors}
          currentProductTypeId={currentProductTypeId}
          sizeGuideButton={
            currentProductTypeId &&
            workId &&
            sizeGuideData != null && (
              <ErrorBoundary>
                <SizeGuideButton
                  productTypeId={currentProductTypeId}
                  workId={workId}
                  key={workId}
                  sizeGuideData={sizeGuideData}
                  initialGender={initialGender}
                  initialMeasurementUnit={initialMeasurementUnit}
                />
              </ErrorBoundary>
            )
          }
        />
      );
    }
    case 'radio-selector':
    case 'select':
    default: {
      const genericOptions = filterValues.map(({ value }) =>
        getGenericOptionConfig(value, locale)
      );

      return (
        <SimpleFieldConfigurator
          fieldType={option.frontendControl as FieldType}
          label={label}
          attributeName={option.key}
          currentValue={currentValue}
          options={genericOptions}
          errors={errors}
          currentProductTypeId={currentProductTypeId}
        />
      );
    }
  }
};
